import React from "react"

import { Button, Grid, Group, NumberInput, Stack, Text } from "@mantine/core"
import { IconCameraCog } from "@tabler/icons-react"

import ModuleSettingsCard from "../module-settings-card/ModuleSettingsCard"
import useCameraModuleSettings from "./useCameraModuleSettings"
import { ModuleSettingsProps } from "../module-settings-card/ModuleSettingsProps"
import _DeviceCameraModuleSettings from "./_DeviceCameraModuleSettings"
import _GroupCameraModuleSettings from "./_GroupCameraModuleSettings"
import { ModuleFC } from "../module-settings-card/ModuleFC"
import SettingsOverwriteWarning from "../settings-overwrite-warning/SettingsOverwriteWarning"
import { useLanguage } from "../../../../contexts/language-context"

type CameraModuleSettingsProps = ModuleSettingsProps<"duration">

const CameraModuleSettings: ModuleFC<CameraModuleSettingsProps> = Object.assign(
    ({ attrs, organisation, group, device }) => {
        const { form, onSubmit, reset, inheritSettings, processing } = useCameraModuleSettings(
            attrs,
            organisation,
            group,
            device,
        )
        const { L } = useLanguage()

        return (
            <ModuleSettingsCard title={L("Camera settings")} icon={IconCameraCog}>
                <form onSubmit={form.onSubmit(onSubmit)}>
                    <Grid gutter="xl">
                        <Grid.Col span={12}>
                            <SettingsOverwriteWarning attrs={attrs} group={group} device={device} />
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Stack spacing={0}>
                                <Text size="sm">
                                    {L("Recording length")}{" "}
                                    {form.values.durationPre !== null && form.values.durationPost !== null && (
                                        form.values.durationPost < 60 ?
                                        `(${L("%s seconds total", form.values.durationPre + form.values.durationPost)})`
                                        : `(${L("%s minutes total", (form.values.durationPre + form.values.durationPost) / 60)})`
                                    )}
                                </Text>
                                <Text size="xs" color="dimmed">
                                    {L("Length of video recording in seconds. Longer recordings use more data.")}
                                </Text>
                                <Grid pt="0.25rem">
                                    <Grid.Col span={12} md={6}>
                                        {device?.type !== "BeeMoni_RPI4" ? (
                                            <NumberInput
                                                placeholder={L("Before detection")}
                                                min={1}
                                                max={30}
                                                {...form.getInputProps("durationPre")}
                                                value={(form.values.durationPre as number | null) ?? ""}
                                            />
                                        ) : (
                                            <>
                                                <NumberInput value={0} disabled />
                                                <Text align="end" size="xs" color="dimmed">
                                                    {L("BeeMoni records continuously")}
                                                </Text>
                                            </>
                                        )}
                                    </Grid.Col>
                                    <Grid.Col span={12} md={6}>
                                        {device?.type !== "BeeMoni_RPI4" ? (
                                            <NumberInput
                                                placeholder={L("After detection")}
                                                min={1}
                                                max={30}
                                                {...form.getInputProps("durationPost")}
                                                value={(form.values.durationPost as number | null) ?? ""}
                                            />
                                        ) : (
                                            <NumberInput
                                                placeholder={L("After detection")}
                                                min={300}
                                                max={1800}
                                                {...form.getInputProps("durationPost")}
                                                value={(form.values.durationPost as number | null) ?? ""}
                                            />
                                        )}
                                    </Grid.Col>
                                </Grid>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Group position="right">
                                {device && (
                                    <Button
                                        variant="subtle"
                                        onClick={inheritSettings}
                                        disabled={form.values.durationPre === null && form.values.durationPost === null}
                                    >
                                        {L("Apply group settings")}
                                    </Button>
                                )}
                                <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                    {L("Reset")}
                                </Button>
                                <Button type="submit" disabled={!form.isDirty()} loading={processing}>
                                    {L("Save")}
                                </Button>
                            </Group>
                        </Grid.Col>
                    </Grid>
                </form>
            </ModuleSettingsCard>
        )
    },
    {
        Device: _DeviceCameraModuleSettings,
        Group: _GroupCameraModuleSettings
    }
)

export default CameraModuleSettings
